import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Block as SkippedIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { getAutomationLogsApi, retryAutomationApi } from "../../../api/automation.api";
import { formatDistanceToNow } from "date-fns";

const RuleLogsDialog = ({ open, onClose, ruleId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    if (open && ruleId) {
      fetchLogs();
    }
  }, [open, ruleId, page, rowsPerPage]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await getAutomationLogsApi({
        ruleId,
        page: page + 1,
        limit: rowsPerPage,
      });
      setLogs(response.data.data.logs || []);
      setTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (logId) => {
    try {
      await retryAutomationApi(logId);
      setAnchorEl(null);
      setSelectedLog(null);
      await fetchLogs();
    } catch (err) {
      console.error("Error retrying automation:", err);
      alert(err.response?.data?.message || "Failed to retry automation");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <SuccessIcon sx={{ fontSize: 15, color: "#16a34a" }} />;
      case "failed":
        return <ErrorIcon sx={{ fontSize: 15, color: "#dc2626" }} />;
      case "pending":
        return <PendingIcon sx={{ fontSize: 15, color: "#f59e0b" }} />;
      case "skipped":
        return <SkippedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />;
      default:
        return null;
    }
  };

  const getStatusChipSx = (status) => {
    const map = {
      sent: { bgcolor: "#dcfce7", color: "#16a34a" },
      failed: { bgcolor: "#fee2e2", color: "#dc2626" },
      pending: { bgcolor: "#fef3c7", color: "#d97706" },
      skipped: { bgcolor: "#f3f4f6", color: "#6b7280" },
    };
    return map[status] || map.skipped;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflowX: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 0.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          Automation Logs
        </Typography>
        <Tooltip title="Refresh logs">
          <IconButton
            onClick={fetchLogs}
            size="small"
            sx={{
              color: "#6b7280",
              "&:hover": { bgcolor: "#f3f4f6" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 0, pb: 1.5, overflowX: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : logs.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.88rem" }}
            >
              No logs found for this rule
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "#f3f4f6",
              overflowX: "hidden",
            }}
          >
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "#f9fafb",
                    "& .MuiTableCell-head": {
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.6rem",
                      letterSpacing: 0.8,
                      color: "#9ca3af",
                      borderBottom: "1px solid #f3f4f6",
                      py: 1,
                      px: 1.5,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell sx={{ width: "11%" }}>Status</TableCell>
                  <TableCell sx={{ width: "16%" }}>Lead</TableCell>
                  <TableCell sx={{ width: "17%" }}>Event</TableCell>
                  <TableCell sx={{ width: "16%" }}>Template</TableCell>
                  <TableCell sx={{ width: "14%" }}>Sent At</TableCell>
                  <TableCell sx={{ width: "16%" }}>Error</TableCell>
                  <TableCell align="right" sx={{ width: "10%" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow
                    key={log._id}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "& .MuiTableCell-root": {
                        py: 1,
                        px: 1.5,
                        fontSize: "0.8rem",
                        borderBottom: "1px solid #f9fafb",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {getStatusIcon(log.execution.status)}
                        <Chip
                          label={log.execution.status.toUpperCase()}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.58rem",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            borderRadius: 1.5,
                            ...getStatusChipSx(log.execution.status),
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        noWrap
                        sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                      >
                        {log.metadata.leadSnapshot?.client_name || "Unknown"}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ fontSize: "0.68rem", color: "#9ca3af" }}
                      >
                        {log.metadata.leadSnapshot?.contact || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.trigger.event}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 500,
                          borderColor: "#e5e7eb",
                          borderRadius: 1.5,
                          color: "#374151",
                          bgcolor: "#f9fafb",
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        {log.execution.templateUsed || "\u2014"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: "0.76rem", color: "#6b7280" }}>
                        {log.execution.sentAt
                          ? formatDistanceToNow(new Date(log.execution.sentAt), {
                              addSuffix: true,
                            })
                          : "\u2014"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.execution.errorMessage ? (
                        <Tooltip title={log.execution.errorMessage}>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.73rem",
                              color: "#dc2626",
                              display: "block",
                            }}
                          >
                            {log.execution.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: "0.76rem", color: "#d1d5db" }}>
                          {"\u2014"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ px: "4px !important" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedLog(log);
                        }}
                        sx={{ color: "#9ca3af" }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination + Close row */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 0.5 }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              overflow: "hidden",
              "& .MuiTablePagination-toolbar": {
                pl: 0,
                minHeight: 36,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
                color: "#6b7280",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.78rem",
              },
            }}
          />
          <Button
            onClick={onClose}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.8rem",
              color: "primary.main",
              flexShrink: 0,
              minWidth: "auto",
              px: 1.5,
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedLog(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 140,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            py: 0.5,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {selectedLog?.execution.status === "failed" && (
          <MenuItem
            onClick={() => handleRetry(selectedLog?._id)}
            sx={{ fontSize: "0.82rem", py: 0.8, px: 2 }}
          >
            Retry
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setSelectedLog(null);
          }}
          sx={{ fontSize: "0.82rem", py: 0.8, px: 2 }}
        >
          View Details
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default RuleLogsDialog;
